import ApiErrorBanner from '@/components/ApiErrorBanner';
import Loading from '@/components/Loading';
import Button from '@/components/UI/Button';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import ObservationScore from '@/components/UI/ObservationScore';
import SelectDropDown from '@/components/UI/SelectDropDown';
import ActionList from '@/components/UI/SelectDropDown/Menus/ActionList';
import Table from '@/components/UI/Table';
import UserAvatar from '@/components/UI/UserAvatar';
import { useFetchEntity } from '@/support/Hooks/fetch';
import { modalAction } from '@/utils/modal';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { _notNil, _isNotBlank } from '@/littledash';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';

const Observations = ({ hasWriteAccess = false }) => {
  const { subjectId } = useParams<{ subjectId: string }>();
  const {
    entity: observations,
    entityLoading: observationsLoading,
    entityError: observationsError,
    fetchEntity: fetchObservations,
  } = useFetchEntity({
    entityType: 'animalObservations',
    params: { id: subjectId },
  });
  const dispatch = useDispatch();
  const { openModal } = modalAction(dispatch);

  const { invoke: deleteObservation } = useLegacyApiHook({
    method: 'delete',
    apiRoute: 'observations.destroy',
    options: { invokeOnInit: false, onError: { slug: 'delete-animal-observation', throw: false, toast: false } },
  });

  const handleObservationClick = (observation: any) => {
    openModal('VIEW_OBSERVATION', { observation });
  };

  const handleEditObservation = (observation: any) => {
    openModal('EDIT_OBSERVATION', {
      observation,
      handleCallback: fetchObservations,
    });
  };

  const handleDeleteObservation = (observation: any) => {
    openModal('CONFIRM_DELETE_OBSERVATION', {
      onClick: async (reason_for_change?: string) => {
        await deleteObservation({
          path: { id: observation.id },
          ...(_notNil(reason_for_change) ? { body: { reason_for_change } } : {}),
        });
        await fetchObservations();
        toast.success('Deleted.');
      },
    });
  };

  const columns = [
    {
      id: 'observation',
      header: 'Name',
      accessorKey: 'title',
      size: 200,
      meta: {
        isVisible: true,
        className: '',
      },
      cell: (info: any) => {
        const observation = info.row.original;
        return (
          <a className="link blue lh-title db pointer" onClick={() => handleObservationClick(observation)}>
            {observation.title}
          </a>
        );
      },
    },
    {
      id: 'score',
      header: 'Score',
      accessorKey: 'value',
      size: 100,
      meta: {
        isVisible: true,
        className: '',
      },
      cell: (info: any) => {
        const observation = info.row.original;
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        return observation.value ? (
          <ObservationScore type={observation.type} score={observation.value} options={observation.options} />
        ) : null;
      },
    },
    {
      id: 'comment',
      header: 'Comment',
      accessorKey: 'text',
      size: 100,
      meta: {
        isVisible: true,
        className: '',
      },
      cell: (info: any) => {
        const text = info.row.original.text;
        return _isNotBlank(text) ? text : '-';
      },
    },
    {
      id: 'recorded',
      header: 'Recorded',
      accessorKey: 'observed_at',
      size: 100,
      meta: {
        isVisible: true,
        className: '',
      },
      cell: (info: any) => {
        const observedAt = info.row.original.observed_at;
        return <DateTimeRenderer value={observedAt} />;
      },
    },
    {
      id: 'recorded_by',
      header: 'User',
      accessorKey: 'created_at.email',
      size: 100,
      meta: {
        isVisible: true,
        className: '',
      },
      cell: (info: any) => {
        const createdBy = info.row.original.created_at;
        return <UserAvatar user={createdBy} tooltip={createdBy.name ?? createdBy.email} />;
      },
    },
    {
      id: 'actions',
      header: '',
      accessorKey: 'actions',
      size: 75,
      enableSorting: false,
      meta: {
        isVisible: hasWriteAccess,
        className: 'ui__table--overflow-cell',
      },
      cell: (info: any) => {
        const observation = info.row.original;
        return (
          <div className="hover-near-black">
            <SelectDropDown
              title="More options"
              iconOnly
              className="hover-near-black"
              alignMenu="right"
              portalProps={{
                width: 120,
              }}
            >
              <ActionList
                actions={[
                  {
                    action: () => handleEditObservation(observation),
                    name: 'Edit',
                  },
                  {
                    action: () => handleDeleteObservation(observation),
                    className: 'red',
                    name: 'Delete',
                  },
                ]}
              />
            </SelectDropDown>
          </div>
        );
      },
    },
  ];

  if (observationsLoading) {
    return <Loading />;
  }

  return (
    <div className="ui-card">
      <div className="pa3 bb b--moon-gray flex justify-between items-center">
        <h3 className="f5 lh-title">Observations</h3>
        <Button
          disabled={!hasWriteAccess}
          onClick={() =>
            openModal('SELECT_OBSERVATIONS', {
              animalId: subjectId,
              handleCallback: fetchObservations,
            })
          }
        >
          Add new
        </Button>
      </div>
      {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
      {observationsError ? (
        <ApiErrorBanner
          title="There was an error fetching observation data"
          text="An error has occurred when fetching observation data, please try again. If this keeps occurring please contact support."
          error={observationsError}
          className="ma4"
        />
      ) : (
        <Table columns={columns} data={observations as any[]} className="tableOverflowVisible" />
      )}
    </div>
  );
};

export default Observations;
